import React, { useState, useRef, lazy } from 'react';
import { motion, useScroll, useTransform, useSpring, useReducedMotion, type MotionValue } from 'framer-motion';
import { DottedLogo } from './DottedLogo';
import { useCopy } from './copy/copyMode';
import { SlackPanel } from './SlackIntegrationGraph';
export function LivesInSlack({
  progress
}: {
  progress?: MotionValue<number>;
}) {
  const copy = useCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const internalScroll = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const scrollYProgress = progress || internalScroll.scrollYProgress;
  // Scroll-linked scale: the panel starts noticeably smaller and visibly
  // expands to full size as the section scrolls up into view, giving the beat
  // a bit of interactivity. It's anchored to the top so it only ever grows
  // downward (never rising over the headline). useTransform clamps outside the
  // input range, so it holds small before it enters and settles at full size
  // once the panel is comfortably in frame.
  const rawScale = useTransform(scrollYProgress, [0.08, 0.5], [0.8, 1.0]);
  const panelScale = useSpring(rawScale, {
    stiffness: 120,
    damping: 24,
    mass: 0.4
  });
  return <section className={`relative w-full overflow-hidden bg-[#FAF8F4] text-[#2B211A] ${progress ? 'h-screen flex flex-col justify-start pt-24 md:pt-28' : 'h-full pt-24 md:pt-28 pb-8 md:pb-20'}`} data-id="element-1954">
      {/* Grainy orange/dark gradient backdrop — using the exact uploaded texture
            without heavy blurs or washes so it matches the provided screenshot. */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1955">
        <img src="https://cdn.magicpatterns.com/uploads/2JtBPCT9d3utTYspgoeqHS/Untitled_design_(6)_1.png" alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" data-id="element-1956" />
      </div>

      {/* Dotted Giga logo background accent */}
      <DottedLogo variant="light" opacity={0.6} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] md:w-[1200px] md:h-[1200px]" data-id="element-1957" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 flex flex-col h-full" data-id="element-1958">
        <div className="text-center mb-6 md:mb-8 shrink-0" data-id="element-1959">
          {/* Heading spans the full centered section width (not the narrow
           paragraph column) and locks to a single line from tablet up. The
           size steps 4xl → 5xl so it always fits on one line at the md
           breakpoint without overflowing. Mobile keeps natural wrapping. */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#2B211A] md:whitespace-nowrap" data-id="element-1960">
            Just @mention Giga in any chat
          </h2>
          <p className="mt-5 md:mt-6 max-w-2xl mx-auto text-base md:text-lg text-neutral-500 font-medium leading-relaxed" data-id="element-1961">
            It pulls answers from every tool you've connected and brings them
            right into the conversation — no tab-switching, no copy-paste.
          </p>
        </div>

        {/* The live Slack / Teams panel: toggles between platforms and hands
          back a generated PDF deliverable inline. Anchored to the top so the
          scroll-scale only ever grows it downward. */}
        <div ref={containerRef} className="relative z-10 mx-auto flex w-full max-w-[720px] flex-1 items-start justify-center" data-id="element-1962">
          <motion.div style={{
          scale: reducedMotion ? 1 : panelScale,
          transformOrigin: 'center top'
        }} className="w-full" data-id="element-1963">
            <SlackPanel data-id="element-1964" />
          </motion.div>
        </div>
      </div>
    </section>;
}